"use client";

import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function Gallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: frameRef,
    isVisible: isFrameVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="gallery" className="bg-background py-12 sm:py-16 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-[1200px] pointer-events-none opacity-30">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl mix-blend-multiply" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary/50 rounded-full blur-3xl mix-blend-multiply" />
      </div>

      <div className="mx-auto max-w-7xl px-6 relative z-10 !h-[1000px] lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center mb-8 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-primary font-semibold tracking-wide uppercase text-sm mb-3">
            Klinik Deneyimi
          </h2>
          <p className="font-serif text-4xl sm:text-5xl text-foreground tracking-tight">
            Lüks ve Konforun Buluşma Noktası
          </p>
        </div>

        <div ref={frameRef} className={`transition-all duration-1000 delay-200 ease-out ${isFrameVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-95"}`}>
          <div className="relative rounded-[2rem] bg-card shadow-2xl shadow-shadow-color/10 border border-border/40 overflow-hidden group">
            <AspectRatio ratio={9 / 16} className="h-[600px] lg:!h-[1000px]">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-background via-card to-muted/50 w-full h-full">
                
                <div className="absolute left-8 lg:left-16 top-1/2 -translate-y-1/2 -rotate-90 origin-center text-muted-foreground/20 font-serif text-4xl lg:text-6xl tracking-widest whitespace-nowrap select-none hidden md:block">
                  AESTHETIC
                </div>
                <div className="absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 rotate-90 origin-center text-muted-foreground/20 font-serif text-4xl lg:text-6xl tracking-widest whitespace-nowrap select-none hidden md:block">
                  DENTISTRY
                </div>

                <div className="relative w-[60%] sm:w-[45%] md:w-[35%] lg:w-[25%] aspect-[9/16] rounded-2xl overflow-hidden shadow-xl shadow-shadow-color/20 border border-white/20 transition-transform duration-700 group-hover:scale-[1.02]">
                  
                  <div className="absolute inset-0 bg-black/10 transition-colors duration-500 group-hover:bg-black/20" />

                  <div className="absolute inset-0 flex items-end justify-center pb-[20%] !bg-[url('https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/video-kapak-fotografi-e1775685152674.png')]">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="icon" className="rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border size-9 w-16 h-16 bg-white/30 backdrop-blur-md border-white/40 text-white transition-all duration-300 shadow-lg !rounded-4xl !m-0 disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 sm:w-20 sm:h-20 hover:bg-white/50 hover:scale-110">
                          <Play className="w-6 h-6 sm:w-8 sm:h-8 ml-1 fill-white" />
                          <span className="sr-only">Videoyu Oynat</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none shadow-2xl">
                        <DialogTitle className="sr-only">Klinik Turu Videosu</DialogTitle>
                        <DialogDescription className="sr-only">
                          Diş Hekimi Ayşegül İnneci Uzun kliniğinin iç mekanını ve lüks atmosferini gösteren tanıtım videosu.
                        </DialogDescription>
                        <AspectRatio ratio={9 / 16} className="bg-black">
                          <iframe className="w-full h-full" src="https://youtube.com/embed/P9MXg_prqCA?si=FL0pZDe8p6PY4Jok" title="Klinik Turu Videosu" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
                        </AspectRatio>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </AspectRatio>
          </div>
        </div>
      </div>
    </section>;
}